/**
 * AI Panel Component
 *
 * A slide-up chat panel for AI interactions.
 * Fixed position at bottom-right, collapsible/expandable.
 */

import { useState, useRef, useEffect, type FormEvent, type KeyboardEvent } from 'react';
import { X, Send, Trash2, Sparkles, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAIPanel } from '@/context/AppContext';
import { openRouter } from '@/services/openRouter';
import { cn } from '@/utils/cn';
import type { AIMessage } from '@/types';

// ============================================
// AI PANEL
// ============================================

export function AIPanel() {
  const { messages, isLoading, isOpen, send, clear, close } = useAIPanel();

  const [input, setInput] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && !isMinimized) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isMinimized]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && !isMinimized && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;

    setInput('');
    await send(trimmedInput);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit(e as unknown as FormEvent);
    }
  };

  // Handle clear conversation
  const handleClear = () => {
    clear();
  };

  // Check if API is configured
  const isConfigured = openRouter.isConfigured();

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed bottom-4 right-4 z-50 flex flex-col',
        'bg-white rounded-lg shadow-2xl border border-gray-200',
        'transition-all duration-300 ease-in-out',
        isMinimized ? 'w-72 h-14' : 'w-96 h-[500px]'
      )}
      role="dialog"
      aria-label="AI Assistant"
    >
      {/* Header */}
      <div
        className={cn(
          'flex items-center justify-between px-4 py-3',
          'border-b border-gray-200 rounded-t-lg',
          'bg-gradient-to-r from-brand-50 to-white'
        )}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-brand-600" />
          <h2 className="font-semibold text-gray-900">AI Assistant</h2>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-brand-600" />}
        </div>

        <div className="flex items-center gap-1">
          {/* Clear button */}
          {messages.length > 0 && !isMinimized && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClear}
              aria-label="Clear conversation"
              className="h-8 w-8"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}

          {/* Minimize/Expand button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMinimized(!isMinimized)}
            aria-label={isMinimized ? 'Expand panel' : 'Minimize panel'}
            className="h-8 w-8"
          >
            {isMinimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>

          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={close}
            aria-label="Close AI panel"
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content (hidden when minimized) */}
      {!isMinimized && (
        <>
          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <EmptyState />
            ) : (
              messages.map((message) => <MessageBubble key={message.id} message={message} />)
            )}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Thinking...</span>
              </div>
            )}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  isConfigured
                    ? 'Ask me anything about your items...'
                    : 'Configure API key in settings first'
                }
                disabled={!isConfigured || isLoading}
                className="min-h-[44px] max-h-32 resize-none"
                rows={1}
                autoResize
              />
              <Button
                type="submit"
                size="icon"
                disabled={!input.trim() || isLoading || !isConfigured}
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>

            <p className="mt-2 text-xs text-gray-500 text-center">
              Powered by {openRouter.getModelDisplayName()}
            </p>
          </form>
        </>
      )}
    </div>
  );
}

// ============================================
// MESSAGE BUBBLE
// ============================================

interface MessageBubbleProps {
  message: AIMessage;
}

function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-4 py-2',
          isUser ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-900'
        )}
      >
        <div className="whitespace-pre-wrap text-sm break-words">{message.content}</div>
        <div className={cn('text-xs mt-1 opacity-70', isUser ? 'text-right' : 'text-left')}>
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
}

// ============================================
// EMPTY STATE
// ============================================

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-4">
      <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center mb-4">
        <Sparkles className="h-6 w-6 text-brand-600" />
      </div>
      <h3 className="font-medium text-gray-900 mb-2">AI Assistant</h3>
      <p className="text-sm text-gray-500 max-w-xs">
        Ask questions about your saved items, get summaries, find connections, or get help
        organizing your knowledge.
      </p>
      <div className="mt-4 space-y-2">
        <SuggestionChip text="What links have I saved recently?" />
        <SuggestionChip text="Summarize my notes about React" />
        <SuggestionChip text="What tasks are incomplete?" />
      </div>
    </div>
  );
}

// ============================================
// SUGGESTION CHIP
// ============================================

interface SuggestionChipProps {
  text: string;
}

function SuggestionChip({ text }: SuggestionChipProps) {
  const { send } = useAIPanel();
  const isConfigured = openRouter.isConfigured();

  const handleClick = () => {
    if (isConfigured) {
      void send(text);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!isConfigured}
      className={cn(
        'text-xs px-3 py-1.5 rounded-full',
        'bg-gray-100 text-gray-700 hover:bg-gray-200',
        'transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
      )}
    >
      {text}
    </button>
  );
}

// ============================================
// HELPERS
// ============================================

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
